"use strict";
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockConverter = void 0;
const prisma_1 = require("@vienna/prisma");
const block_snapshot_entity_1 = require("../entities/block-snapshot.entity");
const PlateConverter_1 = require("./PlateConverter");
// @todo - type assertions everywhere. what is actually required and when?
class BlockConverter {
    /* -------------------------------------------------- */
    /*                    To Snapshot                    */
    /* -------------------------------------------------- */
    static toSnapshot(backendBlock, currentUserId) {
        const baseSnapshot = {
            id: backendBlock.id,
            top: backendBlock.top,
            left: backendBlock.left,
            width: backendBlock.width,
            height: backendBlock.height,
            zIndex: backendBlock.zIndex || 0,
            plateId: backendBlock.pageId,
            createdAt: backendBlock.createdAt.toString(),
            isLiked: backendBlock.isLiked || false,
            connectionCount: backendBlock.numberOfEmbeds || 0,
            currentUserIsOwner: currentUserId ? backendBlock.userId === currentUserId : false,
            userId: backendBlock.userId,
        };
        switch (backendBlock.type) {
            case "text":
                const textSnapshot = {
                    ...baseSnapshot,
                    type: block_snapshot_entity_1.BlockType.Text,
                    textContent: backendBlock.textContent || [],
                    rawContent: backendBlock.rawContent || [],
                    plainText: backendBlock.plainText || "",
                    textType: backendBlock.textSubtype || block_snapshot_entity_1.TextType.Plain,
                };
                return textSnapshot;
            case "image":
                const imageSnapshot = {
                    ...baseSnapshot,
                    type: block_snapshot_entity_1.BlockType.Image,
                    imageFit: backendBlock.imageFit === prisma_1.ImageFit.contain ? block_snapshot_entity_1.ImageFit.Contain : block_snapshot_entity_1.ImageFit.Cover,
                    filename: backendBlock.filename,
                    filesize: backendBlock.fileSize,
                    originalFilename: backendBlock.originalFilename,
                    tags: this.getImageTags(backendBlock),
                    caption: backendBlock.caption || null,
                    hasCaption: !!backendBlock.caption,
                    fileStatus: this.toSnapshotFileStatus(backendBlock),
                    uploadPercent: backendBlock.uploadPercent || 0,
                };
                return imageSnapshot;
            case "color":
                const colorSnapshot = {
                    ...baseSnapshot,
                    type: block_snapshot_entity_1.BlockType.Color,
                    hex: backendBlock.hex,
                };
                return colorSnapshot;
            case "code":
                const codeSnapshot = {
                    ...baseSnapshot,
                    type: block_snapshot_entity_1.BlockType.Code,
                    plainText: backendBlock.plainText || "",
                    language: backendBlock.codeLanguage || block_snapshot_entity_1.ProgrammingLanguage.Javascript,
                };
                return codeSnapshot;
            case "linkEmbed":
            case "spotifyEmbed":
            case "twitterEmbed":
            case "youtubeEmbed":
            case "instagramEmbed":
                const linkSnapshot = {
                    ...baseSnapshot,
                    type: block_snapshot_entity_1.BlockType.Link,
                    linkEmbedType: this.getSnapshotLinkType(backendBlock),
                    url: backendBlock.plainText,
                };
                return linkSnapshot;
            case "file":
                const fileSnapshot = {
                    ...baseSnapshot,
                    type: block_snapshot_entity_1.BlockType.File,
                    filename: backendBlock.filename || null,
                    filesize: backendBlock.fileSize || null,
                    originalFilename: backendBlock.originalFilename || null,
                    fileStatus: this.toSnapshotFileStatus(backendBlock),
                    uploadPercent: backendBlock.uploadPercent,
                };
                return fileSnapshot;
            case "blockWindow":
                const initialBlockSnapshot = backendBlock.embeddedBlock
                    ? this.convertEmbeddedBlock(backendBlock.embeddedBlock, currentUserId)
                    : null;
                const blockWindowSnapshot = {
                    ...baseSnapshot,
                    isLiked: initialBlockSnapshot?.isLiked || false,
                    type: block_snapshot_entity_1.BlockType.BlockWindow,
                    initialBlockSnapshot,
                };
                return blockWindowSnapshot;
            case "pageWindow":
                const initialPlateSnapshot = backendBlock.embeddedPage
                    ? PlateConverter_1.PlateConverter.toBaseSnapshot(backendBlock.embeddedPage, currentUserId)
                    : null;
                const plateWindowSnapshot = {
                    ...baseSnapshot,
                    isLiked: initialPlateSnapshot?.isLiked || false,
                    type: block_snapshot_entity_1.BlockType.PlateWindow,
                    initialPlateSnapshot,
                };
                return plateWindowSnapshot;
            default:
                throw new Error("Unknown block type");
        }
    }
    /* -------------------------------------------------- */
    /*                     To Prisma                      */
    /* -------------------------------------------------- */
    static toPrisma(block) {
        const prismaBlock = {
            id: block.id,
            type: block.type,
            top: block.top,
            left: block.left,
            width: block.width,
            height: block.height,
            zIndex: block.zIndex,
            pageId: block.plateId,
            createdAt: new Date(block.createdAt || ""),
            userId: block.userId,
        };
        switch (block.type) {
            case block_snapshot_entity_1.BlockType.Text: {
                const textBlock = block;
                prismaBlock.textContent = textBlock.textContent;
                prismaBlock.plainText = textBlock.plainText;
                prismaBlock.textSubtype = textBlock.textType;
                break;
            }
            case block_snapshot_entity_1.BlockType.Code: {
                const codeBlock = block;
                prismaBlock.plainText = codeBlock.plainText;
                prismaBlock.codeLanguage = codeBlock.language;
                break;
            }
            case block_snapshot_entity_1.BlockType.Image: {
                const imageBlock = block;
                prismaBlock.filename = imageBlock.filename;
                prismaBlock.fileSize = imageBlock.filesize;
                prismaBlock.originalFilename = imageBlock.originalFilename;
                prismaBlock.imageFit = imageBlock.imageFit.toLowerCase();
                prismaBlock.caption = imageBlock.caption;
                prismaBlock.fileStatus = this.toPrismaFileStatus(imageBlock.fileStatus);
                break;
            }
            case block_snapshot_entity_1.BlockType.Link: {
                const linkBlock = block;
                prismaBlock.plainText = linkBlock.url;
                prismaBlock.type = this.getLinkEmbedType(linkBlock.linkEmbedType);
                break;
            }
            case block_snapshot_entity_1.BlockType.Color: {
                const colorBlock = block;
                prismaBlock.hex = colorBlock.hex;
                break;
            }
            case block_snapshot_entity_1.BlockType.File: {
                const fileBlock = block;
                prismaBlock.filename = fileBlock.filename;
                prismaBlock.fileSize = fileBlock.filesize;
                prismaBlock.originalFilename = fileBlock.originalFilename;
                prismaBlock.fileStatus = this.toPrismaFileStatus(fileBlock.fileStatus);
                break;
            }
            case block_snapshot_entity_1.BlockType.BlockWindow: {
                const blockWindowBlock = block;
                prismaBlock.embeddedBlockId = blockWindowBlock.initialBlockSnapshot?.id;
                break;
            }
            case block_snapshot_entity_1.BlockType.PlateWindow: {
                const plateWindowBlock = block;
                prismaBlock.embeddedPageId = plateWindowBlock.initialPlateSnapshot?.id;
                break;
            }
        }
        return prismaBlock;
    }
    //* -------------------------------------------------- */
    /*                      Helpers                       */
    /* -------------------------------------------------- */
    static getImageTags(backendBlock) {
        const customTags = backendBlock.customTags?.map((customTag) => ({ tag: customTag, type: block_snapshot_entity_1.ImageTagType.Custom })) || [];
        const generatedTags = backendBlock.webDetection?.webEntities?.map((entity) => ({
            tag: entity.description,
            type: block_snapshot_entity_1.ImageTagType.Generated,
        })) || [];
        return [...customTags, ...generatedTags];
    }
    static getLinkEmbedType(embedType) {
        switch (embedType) {
            case block_snapshot_entity_1.LinkEmbedType.Youtube:
                return "youtubeEmbed";
            case block_snapshot_entity_1.LinkEmbedType.Twitter:
                return "twitterEmbed";
            case block_snapshot_entity_1.LinkEmbedType.Instagram:
                return "instagramEmbed";
            case block_snapshot_entity_1.LinkEmbedType.Spotify:
                return "spotifyEmbed";
            case block_snapshot_entity_1.LinkEmbedType.Other:
                return "linkEmbed";
        }
    }
    static getSnapshotLinkType(backendBlock) {
        switch (backendBlock.type) {
            case "linkEmbed":
                return block_snapshot_entity_1.LinkEmbedType.Other;
            case "spotifyEmbed":
                return block_snapshot_entity_1.LinkEmbedType.Spotify;
            case "twitterEmbed":
                return block_snapshot_entity_1.LinkEmbedType.Twitter;
            case "youtubeEmbed":
                return block_snapshot_entity_1.LinkEmbedType.Youtube;
            case "instagramEmbed":
                return block_snapshot_entity_1.LinkEmbedType.Instagram;
            default:
                return block_snapshot_entity_1.LinkEmbedType.Other;
        }
    }
    static toSnapshotFileStatus(backendBlock) {
        if (!backendBlock.filename)
            return block_snapshot_entity_1.FileStatus.None; // prisma file status enum has only "UPLOADED", "UPLOADING", "FAILED". It defaults to UPLOADED. Which is dumb.
        switch (backendBlock.fileStatus) {
            case "UPLOADED":
                return block_snapshot_entity_1.FileStatus.Uploaded;
            case "UPLOADING":
                return block_snapshot_entity_1.FileStatus.Uploading;
            case "FAILED":
                return block_snapshot_entity_1.FileStatus.Failed;
            default:
                return block_snapshot_entity_1.FileStatus.None;
        }
    }
    static toPrismaFileStatus(fileStatus) {
        switch (fileStatus) {
            case block_snapshot_entity_1.FileStatus.Uploaded:
                return "UPLOADED";
            case block_snapshot_entity_1.FileStatus.Uploading:
                return "UPLOADING";
            case block_snapshot_entity_1.FileStatus.Failed:
                return "FAILED";
            case block_snapshot_entity_1.FileStatus.None:
                return null;
        }
    }
    static convertEmbeddedBlock(block, currentUserId) {
        if (block.type === block_snapshot_entity_1.BlockType.BlockWindow || block.type === block_snapshot_entity_1.BlockType.PlateWindow) {
            throw new Error("Cannot convert embedded block");
        }
        return this.toSnapshot(block, currentUserId);
    }
}
exports.BlockConverter = BlockConverter;
//# sourceMappingURL=BlockConverter.js.map